import { createPortal } from 'react-dom';
import { X, User, Loader2 } from 'lucide-react';
import { useFollowers, useFollowing, useFollowUser } from '@/hooks/useUser';
import { processAvatarUrl } from '@/lib/mediaUtils';
import type { UserProfile } from '@/types';
import { useNavigate } from 'react-router-dom';

interface FollowListProps {
  username: string;
  mode: 'followers' | 'following';
  onClose: () => void;
}

export default function FollowList({ username, mode, onClose }: FollowListProps) {
  const { data: followersData, isLoading: followersLoading } = useFollowers(mode === 'followers' ? username : undefined);
  const { data: followingData, isLoading: followingLoading } = useFollowing(mode === 'following' ? username : undefined);
  const followMutation = useFollowUser();

  const rawData = mode === 'followers' ? followersData : followingData;
  const isLoading = mode === 'followers' ? followersLoading : followingLoading;
  const navigate = useNavigate();

  // SDK returns ApiEnvelope<unknown> — unwrap items
  const items: UserProfile[] = (() => {
    if (!rawData) return [];
    const d = rawData as unknown as Record<string, unknown>;
    if (Array.isArray(d.data)) return d.data as UserProfile[];
    if (Array.isArray(d.items)) return d.items as UserProfile[];
    if (Array.isArray(rawData)) return rawData as UserProfile[];
    return [];
  })();

  return createPortal(
    <div className="fixed inset-0 z-[95] bg-black/70 flex items-end" onClick={onClose}>
      <div
        className="w-full bg-[#0F1117] rounded-t-[24px] flex flex-col"
        style={{ maxHeight: '75vh', paddingBottom: 'env(safe-area-inset-bottom, 0px)', animation: 'slideUpModal 0.25s ease-out' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-[#2A2F3A] rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <h2 className="text-base font-semibold text-[#F5F7FF] capitalize" style={{ fontFamily: 'Space Grotesk' }}>
            {mode}
          </h2>
          <button aria-label="Close" onClick={onClose} className="w-9 h-9 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]">
            <X size={18} />
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 size={28} className="animate-spin text-[#A38BFF]" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <User size={40} className="text-[#2A2F3A] mx-auto mb-3" />
              <p className="text-sm text-[#A7B0C8]">
                {mode === 'followers' ? 'No followers yet' : 'Not following anyone yet'}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {items.map((u) => {
                const uRecord = u as Record<string, unknown>;
                const name = (uRecord.displayName as string) || (uRecord.name as string) || u.username || 'Unknown';
                const rawAvatar = (uRecord.avatarUrl as string) || (uRecord.avatar as string) || '';
                const avatar = processAvatarUrl(rawAvatar);
                const profileEngagement = uRecord.profileEngagement as Record<string, unknown> | undefined;
                const isFollowing = profileEngagement?.isFollowing as boolean | undefined;

                return (
                  <div
                    key={u.ulid}
                    className="flex items-center gap-3 py-2 px-2 -mx-2 rounded-lg transition-colors hover:bg-white/[0.04] active:bg-white/[0.06]"
                    onClick={() => u.username && navigate(`/profile/${u.username}`)}
                  >
                    {avatar ? (
                      <img src={avatar} alt={name} className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-[#141821] flex items-center justify-center flex-shrink-0">
                        <User size={18} className="text-[#A7B0C8]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#F5F7FF] truncate">{name}</p>
                      {u.username && <p className="text-xs text-[#A7B0C8]">@{u.username}</p>}
                    </div>
                    {u.username && mode === 'followers' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          followMutation.mutate({ username: u.username!, isFollowing: isFollowing ?? false });
                        }}
                        className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                          isFollowing
                            ? 'bg-[#141821] text-[#A7B0C8] border border-white/[0.08]'
                            : 'bg-[#A38BFF] text-[#0B0D10]'
                        }`}
                      >
                        {isFollowing ? 'Following' : 'Follow'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
